import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { useParams } from 'react-router-dom';

import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import SuspenseWithLoader from '../SuspenseWithLoader';
import PDPPlaceholder from '../PDP/PDPPlaceholder';
import ProductListingPagePlaceholder from '../ProductListingPage/ProductListingPagePlaceholder';
import { isStyleCategoryPrefix } from '../category-page-algolia/categoryPageSlugUtils';

const CategoryPage = loadable(() => import('../CategoryPage'), {
  loading: ProductListingPagePlaceholder,
});

// Code-split loading only — PDP owns hero vs context Suspense boundaries.
const PDP = loadable(() => import('../PDP'), {
  loading: PDPPlaceholder,
});

const ProductAndCategory = props => {
  const { category: categoryFromPath, slug: slugFromPath } = useParams();

  const {
    category: categoryOverride,
    slug: slugOverride,
    isAdmin,
    productOptionsPromise,
    productHeroImagePromise,
    productCylindoHeroImagePromise,
    productBasicDetailsPromise,
    serverCategoryData,
  } = props;

  const category = categoryOverride || categoryFromPath;
  const slug = slugOverride || slugFromPath;

  // Keep the PDP instance stable while deep-link query params change.
  // The PDP reads query params from router state, so the full tree does not
  // need to remount when a user changes fabrics or other options.
  const pdpKey = slug;

  if (isAdmin) {
    return (
      <AdminPlaceholder>
        ProductAndCategory Component Placeholder for Variant Builder. <br />
        The component will display the product page correctly in Consumer
      </AdminPlaceholder>
    );
  }

  if (isStyleCategoryPrefix(category)) {
    const categoryPageSlug = `${category}/${slug}`;

    return (
      <SuspenseWithLoader
        fallback={<ProductListingPagePlaceholder />}
        key={categoryPageSlug}
      >
        <CategoryPage
          key={categoryPageSlug}
          slug={categoryPageSlug}
          serverCategoryData={serverCategoryData}
        />
      </SuspenseWithLoader>
    );
  }

  // Hero vs context Suspense boundaries live inside PDP (not a full-page
  // PDPPlaceholder wrapper here). Loadable `loading: PDPPlaceholder` still
  // covers the code-split gap only.
  return (
    <PDP
      key={pdpKey}
      productOptionsPromise={productOptionsPromise}
      productHeroImagePromise={productHeroImagePromise}
      productCylindoHeroImagePromise={productCylindoHeroImagePromise}
      productBasicDetailsPromise={productBasicDetailsPromise}
    />
  );
};

ProductAndCategory.propTypes = {
  isAdmin: PropTypes.bool,
  category: PropTypes.string,
  slug: PropTypes.string,
  productOptionsPromise: PropTypes.object,
  productHeroImagePromise: PropTypes.object,
  productCylindoHeroImagePromise: PropTypes.object,
  productBasicDetailsPromise: PropTypes.object,
  serverCategoryData: PropTypes.object,
};

export default ProductAndCategory;
